import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import moment from "moment";
import "./ServiceDashBoard.css";

import { toast } from "react-toastify";
import {
  FiChevronRight,
  FiAlertTriangle,
  FiX,
  FiMail,
  FiPhone,
  FiExternalLink,
  FiCalendar,
  FiClock,
  FiDownload,
  FiShare2,
  FiAward,
  FiCopy,
  FiStar,
  FiGift,
  FiCheckCircle,
} from "react-icons/fi";
import { IoShareSocialOutline, IoLogoWhatsapp } from "react-icons/io5";
import {
  getServiceDashBoard,
  claimReward,
} from "../../../redux/actions/calendarAction";
import { API_URL, BASE_URL, JITSI_URL } from "../../../config/app-config";
import SocialShare from "../../../components/SocialShare";
import { mergeOfferedHours } from "./timeSlotUtils";

const MOBILE_BREAKPOINT = 768;
const REFERRAL_CLAIM_THRESHOLD = 100;

const DAY_NAMES = {
  SUN: "Sunday",
  MON: "Monday",
  TUE: "Tuesday",
  WED: "Wednesday",
  THU: "Thursday",
  FRI: "Friday",
  SAT: "Saturday",
};

const REWARD_OPTIONS = [
  {
    id: "ai_credits",
    label: "AI Credits",
    desc: "Redeem points as usable AI generation credits.",
    icon: <FiGift />,
  },
  {
    id: "wallet_cash",
    label: "Wallet Cash",
    desc: "Redeem points as balance in your payout wallet.",
    icon: <FiAward />,
  },
];

function getStatusColor(status) {
  switch ((status || "").toLowerCase()) {
    case "upcoming":
      return "var(--success)";
    case "cancelled":
      return "#ef4444";
    case "completed":
      return "#2563eb";
    case "ongoing":
      return "var(--warning)";
    default:
      return "var(--text-muted)";
  }
}

function parseFullTime(timeSlot) {
  if (!timeSlot) return "";
  try {
    const s = JSON.parse(timeSlot);
    const f = s?.[0]?.from,
      to = s?.[0]?.to;
    const ff = f ? moment(f, "HH:mm").format("h:mm A") : "";
    const tt = to ? moment(to, "HH:mm").format("h:mm A") : "";
    return tt ? `${ff} – ${tt}` : ff;
  } catch {
    return "";
  }
}

/**
 * Sums claimable points and returns claim eligibility.
 * Your backend's only referral states are PENDING -> CLAIMED (no
 * "COMPLETED"), so PENDING + unclaimed is the claimable set.
 */
function getReferralSummary(referralDetails = []) {
  const claimable = referralDetails.filter(
    (r) => r.status === "PENDING" && !r.claimedAt
  );
  const claimablePoints = claimable.reduce((sum, r) => sum + (r.points || 0), 0);
  const claimableIds = claimable.map((r) => r.id);

  const claimedPoints = referralDetails
    .filter((r) => r.status === "CLAIMED")
    .reduce((sum, r) => sum + (r.points || 0), 0);

  return {
    claimablePoints,
    claimableIds,
    claimedPoints,
    eligible: claimablePoints >= REFERRAL_CLAIM_THRESHOLD,
    progressPct: Math.min(
      100,
      Math.round((claimablePoints / REFERRAL_CLAIM_THRESHOLD) * 100)
    ),
  };
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

function LedgerRow({ item }) {
  return (
    <button type="button" className="home-ledger-row" onClick={item.action}>
      <span className={`ledger-chip ${item.ready ? "is-ready" : "is-needed"}`}>
        {item.ready ? "Ready" : "Needs you"}
      </span>
      <span className="home-ledger-id">{item.id}</span>
      <span className="home-ledger-info">
        <span className="home-ledger-label">{item.label}</span>
        <span className="home-ledger-desc">{item.desc}</span>
      </span>
      <span className="home-ledger-link">
        {item.ready ? "Review" : "Open"}
        <FiChevronRight size={16} />
      </span>
    </button>
  );
}

async function downloadQrImage(url) {
  try {
    const r = await fetch(url);
    const b = await r.blob();
    const l = document.createElement("a");
    l.href = URL.createObjectURL(b);
    l.download = "service-profile-qr.png";
    document.body.appendChild(l);
    l.click();
    document.body.removeChild(l);
  } catch {
    toast.error("Error downloading the QR code");
  }
}

function ShareProfileModal({ open, onClose, slug, referralCode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const qrUrl = encodeURIComponent(`${BASE_URL}yuukke-service/${slug}`);
  const qrImg = `${API_URL}auth/v2/qrcode/image?url=${qrUrl}`;

  const links = [
    {
      title: "Booking Link",
      icon: <FiShare2 />,
      accent: "blue",
      url: `${BASE_URL}yuukke-service/${slug}`,
      label: "Booking link copied!",
    },
    {
      title: "Recommendation Link",
      icon: <FiAward />,
      accent: "amber",
      url: `${BASE_URL}service-recommend/${slug}`,
      label: "Recommendation link copied!",
    },
    ...(referralCode
      ? [
          {
            title: "Referral Link",
            icon: <FiCopy />,
            accent: "green",
            url: `${BASE_URL}service-referral/${referralCode}`,
            label: "Referral link copied!",
          },
        ]
      : []),
  ];

  const handleCopy = (url, label) => {
    navigator.clipboard.writeText(url);
    toast.success(label);
  };

  return (
    <div className="sm-overlay" onClick={onClose}>
      <div
        className="sm-panel sm-panel--v2"
        role="dialog"
        aria-modal="true"
        aria-label="Share profile"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sm-drag-handle" aria-hidden="true" />

        <div className="sm-header sm-header--v2">
          <div>
            <h3 className="sm-header-title">Share Profile</h3>
            <span className="sm-header-sub">Expand your network and get bookings.</span>
          </div>
          <button className="sm-close-btn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <div className="sm-body sm-body--v2">
          {slug ? (
            <React.Fragment>
              {/* QR block */}
              <div className="sm-qr-v2">
                <img src={qrImg} alt="QR code" className="sm-qr-v2-img" />
                <div className="sm-qr-v2-text">
                  <span className="sm-qr-v2-title">Your QR Code</span>
                  <span className="sm-qr-v2-sub">Scan to book instantly</span>
                </div>
                <button
                  className="sm-btn-v2 sm-btn-v2--ghost"
                  onClick={() => downloadQrImage(qrImg)}
                  aria-label="Download QR code"
                >
                  <FiDownload />
                  <span>Save</span>
                </button>
              </div>

              {/* Link list */}
              <div className="sm-link-list-v2">
                {links.map((item) => (
                  <div className="sm-link-row-v2" key={item.title}>
                    <span className={`sm-row-icon-v2 sm-row-icon-v2--${item.accent}`}>
                      {item.icon}
                    </span>
                    <div className="sm-link-row-v2-main">
                      <span className="sm-link-row-v2-title">{item.title}</span>
                      <span className="sm-link-row-v2-url">
                        {item.url.replace(/^https?:\/\//, "")}
                      </span>
                    </div>
                    <button
                      className="sm-btn-v2 sm-btn-v2--copy"
                      onClick={() => handleCopy(item.url, item.label)}
                      aria-label={`Copy ${item.title}`}
                    >
                      <FiCopy />
                    </button>
                  </div>
                ))}
              </div>

              {/* Social row */}
              <div className="sm-social-v2">
                <span className="sm-social-v2-label">Share with your network</span>
                <div className="sm-social-v2-icons">
                  <SocialShare
                    closeNotNeed={true}
                    url={links[0].url}
                    title="Check my available schedule"
                    closeModel={() => {}}
                  />
                </div>
              </div>
            </React.Fragment>
          ) : (
            <div className="home-empty-state">Set up your public page slug before sharing.</div>
          )}
        </div>

        <div className="sm-footer sm-footer--v2">
          {slug && (
              <a
              className="sm-footer-preview"
              href={`${BASE_URL}yuukke-service/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FiExternalLink size={14} /> Preview page
            </a>
          )}
          <button className="bm-footer-btn unified-secondary-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewStars({ rating = 0 }) {
  return (
    <span className="bm-stars" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <FiStar
          key={n}
          size={14}
          className={n <= rating ? "bm-star bm-star--filled" : "bm-star"}
        />
      ))}
    </span>
  );
}

/**
 * No review-collection stepper here on purpose — for a completed session
 * with no review yet, the provider gets exactly one action: nudge the
 * client on WhatsApp. Falls back to a blank-recipient wa.me link (opens
 * the picker) if we don't have a phone number for this booking.
 */
function buildReviewRequestWhatsAppUrl(booking) {
  const digits = (booking.mobileNumber || "").replace(/[^\d]/g, "");
  const clientName = booking.userName ? `Hi ${booking.userName}, ` : "Hi, ";
  const service = booking.serviceName || "your session";
  const message =
    `${clientName}thanks again for booking ${service}! ` +
    `If you have a minute, a quick review would really help others find us. 🙏`;
  const base = digits ? `https://wa.me/${digits}` : "https://wa.me/";
  return `${base}?text=${encodeURIComponent(message)}`;
}

function BookingDetailRow({ icon: Icon, label, children }) {
  return (
    <div className="bm-row">
      <span className="bm-row-label">
        {Icon && <Icon size={14} className="bm-row-icon" />}
        {label}
      </span>
      <div className="bm-row-value">{children}</div>
    </div>
  );
}

const BookingDetailModal = React.memo(function BookingDetailModal({ open, onClose, booking }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open || !booking) return null;

  const name = booking.userName || booking.email || "Client";
  const service = booking.serviceName || "Session";
  const email = booking.email || "";
  const phone = booking.mobileNumber || "";
  const amount = booking.amount ?? 0;
  const status = booking.status || "Confirmed";
  const meetingUrl = booking.url || "";
  const duration = booking.meetingActiveDuration || "";
  const sessions = booking.totalSessions || 1;
  const apptType = booking.appointmentType || "";
  const statusColor = getStatusColor(status);

  const dateMom = booking.startTime ? moment(booking.startTime) : null;
  // Ensure English locale for modal date format
  const dateStr = dateMom?.isValid() ? dateMom.locale("en").format("dddd, D MMMM YYYY") : "";
  const timeStr = parseFullTime(booking.timeSlot);
  const durLabel = duration ? ` (${duration} min)` : "";

  return (
    <div className="bm-overlay" onClick={onClose}>
      <div
        className="bm-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Booking details"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bm-drag-handle" aria-hidden="true" />
        <div className="bm-header">
          <div className="bm-header-main">
            <div className="bm-avatar">{name[0]?.toUpperCase()}</div>
            <div className="bm-header-text">
              <span className="bm-header-eyebrow">Booking details</span>
              <h3 className="bm-header-name">{name}</h3>
              {apptType && <span className="bm-header-sub">{apptType}</span>}
            </div>
          </div>
          <button className="bm-close-btn" onClick={onClose} aria-label="Close">
            <FiX size={20} />
          </button>
        </div>

        <div className="bm-body">
          <BookingDetailRow label="Service">
            <span className="bm-val">{service}</span>
          </BookingDetailRow>

          {email && (
            <BookingDetailRow icon={FiMail} label="Email">
              <div className="bm-val-row">
                <span className="bm-val">{email}</span>
                <a href={`mailto:${email}`} className="bm-action-chip bm-action-chip--mail">
                  <FiMail size={12} /> Email
                </a>
              </div>
            </BookingDetailRow>
          )}

          {phone && (
            <BookingDetailRow icon={FiPhone} label="Phone">
              <div className="bm-val-row">
                <span className="bm-val">{phone}</span>
                <a href={`tel:${phone}`} className="bm-action-chip bm-action-chip--call">
                  <FiPhone size={12} /> Call
                </a>
              </div>
            </BookingDetailRow>
          )}

          <BookingDetailRow label="Amount">
            <span
              className="bm-val bm-val--amount"
              style={{ color: amount > 0 ? "var(--success)" : "var(--text-muted)" }}
            >
              {amount > 0 ? `₹${Number(amount).toLocaleString()}` : "Free / Unpaid"}
            </span>
          </BookingDetailRow>

          {sessions > 1 && (
            <BookingDetailRow label="Sessions">
              <div className="bm-val-row">
                <span className="bm-val">{sessions} sessions</span>
                <span className="bm-tag">Group package</span>
              </div>
            </BookingDetailRow>
          )}

          {(dateStr || timeStr) && (
            <BookingDetailRow icon={FiCalendar} label="Scheduled">
              <span className="bm-val">
                {dateStr}
                {timeStr ? ` · ${timeStr}` : ""}
                {durLabel}
              </span>
            </BookingDetailRow>
          )}

          <BookingDetailRow icon={FiClock} label="Meeting link">
            {meetingUrl ? (
                <a
                href={
                  booking.meetingInApplication
                    ? `${JITSI_URL}/${meetingUrl}?isGroup=true&sp=yes#config`
                    : meetingUrl
                }
                target="_blank"
                rel="noopener noreferrer"
                className="bm-action-chip bm-action-chip--join"
              >
                <FiExternalLink size={12} /> Join meeting
              </a>
            ) : (
              <span className="bm-val bm-val--muted">No link available</span>
            )}
          </BookingDetailRow>

          <BookingDetailRow label="Status">
            <span className="bm-status" style={{ color: statusColor }}>
              <span className="bm-status-dot" style={{ background: statusColor }} />
              {status}
            </span>
          </BookingDetailRow>

          {/* Review only makes sense once the session is done. */}
          {status.toLowerCase() === "completed" && (
            <BookingDetailRow icon={FiStar} label="Review">
              {booking.review ? (
                <div className="bm-review">
                  <ReviewStars rating={booking.review.rating} />
                  {booking.review.comment && (
                    <span className="bm-val bm-val--review">"{booking.review.comment}"</span>
                  )}
                </div>
              ) : (
                 <a
                  href={buildReviewRequestWhatsAppUrl(booking)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bm-action-chip bm-action-chip--whatsapp"
                >
                  <IoLogoWhatsapp size={14} /> Send review request
                </a>
              )}
            </BookingDetailRow>
          )}
        </div>

        <div className="bm-footer">
          <button className="bm-footer-btn unified-secondary-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
});

function ClaimRewardModal({ open, onClose, claimablePoints, onConfirm, submitting }) {
  const [selected, setSelected] = useState(REWARD_OPTIONS[0].id);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="sm-overlay" onClick={onClose}>
      <div
        className="rw-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Claim referral reward"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sm-drag-handle" aria-hidden="true" />
        <div className="sm-header sm-header--v2">
          <div>
            <h3 className="sm-header-title">Claim Reward</h3>
            <span className="sm-header-sub">
              You have {claimablePoints} points ready to claim.
            </span>
          </div>
          <button className="sm-close-btn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <div className="rw-body">
          {REWARD_OPTIONS.map((opt) => (
            <button
              type="button"
              key={opt.id}
              className={`rw-option ${selected === opt.id ? "rw-option--selected" : ""}`}
              onClick={() => setSelected(opt.id)}
            >
              <span className="rw-option-icon">{opt.icon}</span>
              <span className="rw-option-text">
                <span className="rw-option-title">{opt.label}</span>
                <span className="rw-option-desc">{opt.desc}</span>
              </span>
              {selected === opt.id && <FiCheckCircle className="rw-option-check" />}
            </button>
          ))}
        </div>

        <div className="sm-footer sm-footer--v2">
          <button
            className="bm-footer-btn unified-secondary-btn"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="unified-primary-btn"
            onClick={() => onConfirm(selected)}
            disabled={submitting}
          >
            {submitting ? "Claiming…" : "Confirm claim"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReferralRewardsCard({ referralDetails, onClaimClick }) {
  const { claimablePoints, pendingPoints, eligible, progressPct } = useMemo(
    () => getReferralSummary(referralDetails),
    [referralDetails]
  );

  return (
    <div className="home-card">
      <div className="home-card-eyebrow-row">
        <div className="home-card-eyebrow">Referral rewards</div>
        <span className="home-ledger-count">{referralDetails.length} referrals</span>
      </div>
      <h3 className="home-card-heading">
        {claimablePoints} / {REFERRAL_CLAIM_THRESHOLD} points
      </h3>
      <div className="rw-progress-track">
        <div className="rw-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>
      <p className="home-card-desc">
        {eligible
          ? "You've reached the claim threshold."
          : `${REFERRAL_CLAIM_THRESHOLD - claimablePoints} more points to unlock a claim.`}
      </p>

      {referralDetails.length > 0 && (
        <div className="home-activity-list rw-referral-list">
          {referralDetails.slice(0, 4).map((r) => (
            <div className="home-activity-row" key={r.id}>
              <div
                className="home-activity-dot"
                style={{
                  background: r.status === "CLAIMED" ? "var(--success)" : "var(--warning)",
                }}
              />
              <div className="home-activity-info">
                <div className="home-activity-title">Referred user #{r.referredUserId}</div>
                <div className="home-activity-meta">
                  {r.status} · {r.points} pts
                  {r.claimedAt ? " · claimed" : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        className="unified-primary-btn"
        disabled={!eligible}
        onClick={onClaimClick}
        style={{ marginTop: "12px" }}
      >
        {eligible ? "Claim reward" : "Not eligible yet"}
      </button>
    </div>
  );
}

const HomeDashboard = ({ t, makeAction }) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimSubmitting, setClaimSubmitting] = useState(false);

  const { loginDetails } = useSelector((s) => s.loginDetailsApi);
  const { serviceDashboard, userSlots, activityFeed } = useSelector((s) => s.event);

  const preference = userSlots?.preference;
  const calendarId = preference?.id;
  const slotsAvailable = userSlots?.slots || [];

  useEffect(() => {
    if (calendarId) dispatch(getServiceDashBoard({ calendarId }));
  }, [calendarId, dispatch]);

  const providerFirstName = (loginDetails?.data?.name || "there").split(" ")[0];

  // Ensure explicit English locale format for main header date
  const dateHeader = moment().locale("en").format("dddd, D MMMM YYYY").toUpperCase();

  const nextMeet = serviceDashboard?.nextMeet;
  const hasNextMeet = !!nextMeet && nextMeet.status !== "cancelled";
  const nextMeetTimeSlot = useMemo(() => {
    if (!nextMeet?.timeSlot) return "";
    try {
      const s = JSON.parse(nextMeet.timeSlot)[0];
      if (!s) return "";
      const f = moment(s.from, ["h:mm a", "HH:mm"]).format("HH:mm");
      const to = moment(s.to, ["h:mm a", "HH:mm"]).format("HH:mm");
      return `${f}–${to}`;
    } catch {
      return "";
    }
  }, [nextMeet]);

  const approvalStatus = preference?.approvalStatus ?? "pending";
  const isPending = approvalStatus === "pending";

  const hoursText = slotsAvailable.length
    ? slotsAvailable
        .map((slot) => {
          const dayName = DAY_NAMES[slot.day] || slot.day;
          const from = slot.timeSlots?.[0]?.from || "--:--";
          const to = slot.timeSlots?.[0]?.to || "--:--";
          return `${dayName} · ${from}–${to}`;
        })
        .join(", ")
    : "Not set yet";

  const DEFAULT_SERVICE_NAME = "My Service";
  const DEFAULT_DESCRIPTION = "Service provider on Yuukke";

  function stripHtml(value) {
    return (value || "").replace(/<[^>]*>/g, "").trim();
  }

  function isRealValue(value, defaultValue) {
    const clean = stripHtml(value);
    return clean.length > 0 && clean !== defaultValue;
  }

  const ledgerItems = [
    {
      id: "01",
      label: "Your public identity",
      desc: "Name and introduction visitors will see.",
      ready: isRealValue(preference?.serviceName, DEFAULT_SERVICE_NAME) &&
             isRealValue(preference?.description, DEFAULT_DESCRIPTION),
      action: () => makeAction(0),
    },
    {
      id: "02",
      label: "Your service offer",
      desc: preference?.serviceName || "Add your service details.",
      ready: !!preference?.serviceName,
      action: () => makeAction(5),
    },
    {
      id: "03",
      label: "Your offered hours",
      desc: slotsAvailable.length ? hoursText : "Set your available hours.",
      ready: slotsAvailable.length > 0,
      action: () => makeAction(4),
    },
    {
      id: "04",
      label: "Payout readiness",
      desc: "A private acknowledgement is required before publishing.",
      ready: !!(preference?.accountNumber || preference?.bankDetailsType),
      action: () => makeAction(0),
    },
  ];

  const readyCount = ledgerItems.filter((i) => i.ready).length;
  const recentActivity = activityFeed || [];
  const hasReview = (serviceDashboard?.needsReviewCount || 0) > 0;
  const reviewerName = serviceDashboard?.latestReview?.userName || "A client";
  const reviewService = serviceDashboard?.latestReview?.serviceName || "session";

  // Referral code for the Share Profile modal — sourced from the dashboard payload.
  // If the API uses a different key, update just this line.
  const referralCode = serviceDashboard?.referralCode;
  const referralDetails = serviceDashboard?.referralDetails || [];
  const { claimablePoints, claimableIds, eligible } = useMemo(
    () => getReferralSummary(referralDetails),
    [referralDetails]
  );

  // Merged, compact pills — "Sun 10a–1p" instead of five separate
  // "Sunday · 10:00–11:00" pills. useMemo so the merge only re-runs when
  // the underlying slots actually change, not on every render.
  const mergedOfferedHours = useMemo(
    () => mergeOfferedHours(slotsAvailable),
    [slotsAvailable]
  );

  const renderOfferedHoursChips = () => {
    if (!mergedOfferedHours.length) {
      return <div className="home-empty-state">No recurring hours set yet.</div>;
    }
    return (
      <div className="home-hours-chips">
        {mergedOfferedHours.map((entry) => (
          <span className="home-hours-chip" key={entry.key}>
            {entry.label}
          </span>
        ))}
      </div>
    );
  };

  const renderActivityList = (limit) => {
    if (!recentActivity.length) {
      return <div className="home-empty-state">No recent activity.</div>;
    }
    return (
      <div className="home-activity-list">
        {recentActivity.slice(0, limit).map((ev, i) => (
          <div className="home-activity-row" key={ev.id ?? i}>
            <div className="home-activity-dot" />
            <div className="home-activity-info">
              <div className="home-activity-title">{ev.title}</div>
              <div className="home-activity-meta">
                {ev.actorName} · {moment(ev.createdAt).fromNow()}
              </div>
            </div>
            {ev.badge && <span className="home-activity-badge">{ev.badge}</span>}
          </div>
        ))}
      </div>
    );
  };

  const handleConfirmClaim = async (rewardType) => {
    setClaimSubmitting(true);
    try {
      await dispatch(
        claimReward({
          referralIds: claimableIds,
          rewardType: rewardType.toUpperCase(), // "ai_credits" -> "AI_CREDITS"
        })
      );
      toast.success(
        `${claimablePoints} points claimed as ${
          rewardType === "ai_credits" ? "AI Credits" : "Wallet Cash"
        }!`
      );
      setClaimModalOpen(false);
      if (calendarId) dispatch(getServiceDashBoard({ calendarId })); // refresh totals
    } catch (err) {
      toast.error(err?.message || "Couldn't claim your reward. Please try again.");
    } finally {
      setClaimSubmitting(false);
    }
  };

  const renderMobileLayout = () => (
    <div className="home-wrap home-wrap--mobile">
      <div className="home-container home-container--mobile">
        <div className="home-mobile-heading anim-fade-up">
          <span className="home-date">{dateHeader}</span>
          <h1 className="home-title">{providerFirstName}, what is next?</h1>
          <p className="home-sub">One place to see what needs you and where to continue.</p>
          <button
            type="button"
            className="unified-secondary-btn home-view-public-btn--mobile"
            onClick={() => setShareModalOpen(true)}
          >
            <IoShareSocialOutline size={18} /> View public page
          </button>
        </div>

        <div className="home-card anim-fade-up" style={{ animationDelay: "0.05s" }}>
          <div className="home-card-eyebrow">Next commitment</div>
          {hasNextMeet ? (
            <React.Fragment>
              <h3 className="home-card-heading">{nextMeet.serviceName || "Session"}</h3>
              <div className="home-commit-text-stack">
                <span className="home-commit-name">{nextMeet.userName || "Client"}</span>
                <span className="home-commit-time">
                  {/* Ensure English locale for commitment date */}
                  {moment(nextMeet.startTime).locale("en").format("dddd, D MMMM")}
                  {nextMeetTimeSlot ? ` · ${nextMeetTimeSlot}` : ""}
                </span>
              </div>
              <div className="home-commit-pill-row">
                <span className="home-status-pill">Confirmed</span>
                <button
                  type="button"
                  className="home-view-details-link"
                  onClick={() => setBookingModalOpen(true)}
                >
                  View details
                </button>
              </div>
              <button
                type="button"
                className="unified-primary-btn home-card-action--block"
                onClick={() => makeAction(10)}
              >
                Open Calendar
              </button>
            </React.Fragment>
          ) : (
            <div className="home-empty-state">No upcoming sessions booked yet.</div>
          )}
        </div>

        {hasReview && (
          <div
            className="home-alert-banner home-alert-banner--compact anim-fade-up"
            style={{ animationDelay: "0.1s" }}
          >
            <FiAlertTriangle size={20} className="home-alert-icon" />
            <div className="home-alert-text">
              <span className="home-alert-title">Booking request needs review</span>
              <span className="home-alert-sub">
                {reviewerName} · {reviewService}
              </span>
            </div>
          </div>
        )}

        <div className="home-card anim-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="home-card-eyebrow">Your offered hours</div>
          <h3 className="home-card-heading">Recurring availability</h3>
          <p className="home-card-desc">Times shown in Asia/Kolkata.</p>
          {renderOfferedHoursChips()}
          <button className="unified-secondary-btn" onClick={() => makeAction(4)}>
            Review availability
          </button>
        </div>

        <ReferralRewardsCard
          referralDetails={referralDetails}
          onClaimClick={() => setClaimModalOpen(true)}
        />

        <div className="home-card anim-fade-up" style={{ animationDelay: "0.2s" }}>
          <div className="home-card-eyebrow-row">
            <h3 className="home-card-heading">Recent activity</h3>
            <span className="home-ledger-count">3 days</span>
          </div>
          {renderActivityList(1)}
          {/* Re-styled text button for mobile activity viewing */}
          <button className="unified-primary-btn" onClick={() => makeAction(10)} style={{ marginTop: '12px' }}>
            View all activity
          </button>
        </div>
      </div>

      <nav className="home-bottom-nav">
        <button className="home-nav-item is-active"><span className="home-nav-dot" />Home</button>
        <button className="home-nav-item" onClick={() => makeAction(10)}>Activity</button>
        <button className="home-nav-item" onClick={() => makeAction(4)}>Calendar</button>
        <button className="home-nav-item" onClick={() => makeAction(5)}>Pages</button>
        <button className="home-nav-item" onClick={() => makeAction(0)}>More</button>
      </nav>
    </div>
  );

  const renderDesktopLayout = () => (
    <div className="home-wrap">
      <div className="home-container">
        <div className="home-header anim-fade-up">
          <div>
            <span className="home-date">PROVIDER WORKSPACE · {dateHeader}</span>
            <h1 className="home-title">Home</h1>
            <p className="home-sub">
              A quick read of what happened, what is next, and where to continue.
            </p>
          </div>
          <button className="unified-secondary-btn" onClick={() => setShareModalOpen(true)}>
            View public page
          </button>
        </div>

        {hasReview && (
          <div className="home-alert-banner anim-fade-up" style={{ animationDelay: "0.05s" }}>
            <div className="home-alert-left">
              <FiAlertTriangle size={22} className="home-alert-icon" />
              <div className="home-alert-text">
                <span className="home-alert-title">One booking request needs a closer look</span>
                <span className="home-alert-sub">
                  {reviewerName} asked about a {reviewService}. Review the source record before treating it as a commitment.
                </span>
              </div>
            </div>
            <button className="unified-primary-btn" onClick={() => makeAction(10)}>Open Activity</button>
          </div>
        )}

        <div className="home-two-col anim-fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="home-card">
            <div className="home-card-eyebrow">Next commitment</div>
            {hasNextMeet ? (
              <React.Fragment>
                <h3 className="home-card-heading">{nextMeet.serviceName || "Session"}</h3>
                <div className="home-commit-client">
                  <div className="home-commit-avatar">
                    {(nextMeet.userName || "?")[0]?.toUpperCase()}
                  </div>
                  <div className="home-commit-text-stack">
                    <span className="home-commit-name">{nextMeet.userName || "Client"}</span>
                    <span className="home-commit-time">
                      {/* Ensure English locale for commitment date */}
                      {moment(nextMeet.startTime).locale("en").format("dddd, D MMMM")}
                      {nextMeetTimeSlot ? ` · ${nextMeetTimeSlot} · IST` : ""}
                    </span>
                  </div>
                  <div className="home-commit-pill-row">
                    <span className="home-status-pill">Confirmed</span>
                    <button
                      type="button"
                      className="home-view-details-link"
                      onClick={() => setBookingModalOpen(true)}
                    >
                      View details
                    </button>
                  </div>
                </div>
                <hr className="home-divider" />
                <div className="home-card-footer">
                  <span className="home-card-hint">Prepare from the client record before the session.</span>
                  <button className="unified-primary-btn" onClick={() => makeAction(10)}>
                    Open Calendar
                  </button>
                </div>
              </React.Fragment>
            ) : (
              <div className="home-empty-state">No upcoming sessions booked yet.</div>
            )}
          </div>

          <div className="home-card">
            <div className="home-card-eyebrow">Public page</div>
            <div className="home-card-heading-row">
              <h3 className="home-card-heading">{isPending ? "Draft in progress" : "Published"}</h3>
              <span className={`home-status-pill ${isPending ? "is-neutral" : "is-live"}`}>
                {isPending ? "Private" : "Live"}
              </span>
            </div>
            <p className="home-card-desc">
              {isPending
                ? "Profile, service, and availability are ready to review. Payout readiness still needs your acknowledgement."
                : "Your public booking page is live and accepting requests."}
            </p>
            <div className="home-card-footer">
              <span className="home-card-hint">
                {readyCount} of {ledgerItems.length} setup sections ready
              </span>
              <button className="unified-primary-btn" onClick={() => makeAction(0)}>
                Continue setup
              </button>
            </div>
          </div>
          
        </div>

        <div className="home-card anim-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="home-card-eyebrow-row">
            <div className="home-card-eyebrow">Your service ledger</div>
            <span className="home-ledger-count">
              {readyCount} of {ledgerItems.length} ready
            </span>
          </div>
          <h3 className="home-card-heading">What is ready, and what needs you</h3>
          <div className="home-ledger-list">
            {ledgerItems.map((item) => (
              <LedgerRow item={item} key={item.id} />
            ))}
          </div>
        </div>

        <div className="home-two-col anim-fade-up" style={{ animationDelay: "0.2s" }}>
          <div className="home-card">
            <div className="home-card-eyebrow">Your offered hours</div>
            <h3 className="home-card-heading">Recurring availability</h3>
            <p className="home-card-desc">Times shown in Asia/Kolkata.</p>
            {renderOfferedHoursChips()}
            <button className="unified-secondary-btn" onClick={() => makeAction(4)}>
              Review availability
            </button>
          </div>

          <div className="home-card">
            <div className="home-card-eyebrow-row">
              <div className="home-card-eyebrow">Last three days</div>
              <span className="home-ledger-count">{recentActivity.length} events</span>
            </div>
            <h3 className="home-card-heading">Recent activity</h3>
            {renderActivityList(2)}
            <button className="unified-primary-btn" onClick={() => makeAction(10)} style={{ marginTop: '12px', alignSelf: 'flex-start' }}>
              View all activity
            </button>
          </div>
        </div>

        <div className="home-two-col anim-fade-up" style={{ animationDelay: "0.25s" }}>
          <ReferralRewardsCard
            referralDetails={referralDetails}
            onClaimClick={() => setClaimModalOpen(true)}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isMobile ? renderMobileLayout() : renderDesktopLayout()}

      <BookingDetailModal
        open={bookingModalOpen}
        onClose={() => setBookingModalOpen(false)}
        booking={nextMeet}
      />
      <ShareProfileModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        slug={preference?.slug}
        referralCode={referralCode}
      />
      <ClaimRewardModal
        open={claimModalOpen}
        onClose={() => !claimSubmitting && setClaimModalOpen(false)}
        claimablePoints={claimablePoints}
        onConfirm={handleConfirmClaim}
        submitting={claimSubmitting}
      />
    </>
  );
};

export default withTranslation()(HomeDashboard);